import React, { useState } from 'react'
import { Upload, UploadFile, UploadProps } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { fileApi, FileDirName } from 'packages/sdks-next/growp'
import { Toast } from '@seiue/ui'
import { AnyValueObj } from 'packages/types'
import { RcFile } from 'antd/lib/upload'
import styled from 'styled-components'
import { downloadFile } from '@seiue/util'

/**
 * 附件上传
 *
 * @param param - 参数
 * @param param.onChange - onChange回调
 * @param param.fileList - 文件列表
 * @param param.isEdit - 是否可编辑
 * @param param.maxCount - 最大上传数量
 * @param param.minCount - 最小上传数量
 * @param param.fileSizeLimit - 单个文件上传体积限制
 * @returns React.ReactNode
 */
export const FileUpload: React.FC<{
  onChange?: () => void
  fileList?: UploadFile[]
  isEdit?: boolean
  maxCount?: number
  minCount?: number
  fileSizeLimit?: number
}> = ({ onChange, fileList, isEdit, maxCount, fileSizeLimit }) => {
  const [OSSData, setOSSData] = useState<AnyValueObj>()

  const getOssSignature = async (file: UploadFile) => {
    const { name, type } = file
    const ext = name.substring(name.lastIndexOf('.') + 1)
    let dirName = FileDirName.File
    if (type && /^image/.test(type)) {
      dirName = FileDirName.Images
    }
    if (type && /^video/.test(type)) {
      dirName = FileDirName.Video
    }
    const { data } = await fileApi.getOssSignature(ext, dirName)
    setOSSData(data)
    return data
  }

  const beforeUpload: UploadProps['beforeUpload'] = async (
    file: RcFile & { url?: string }
  ) => {
    if (maxCount && fileList && fileList.length >= maxCount) {
      Toast.warning(`最多上传${maxCount}个文件`)
      return false
    }
    const fileSizeM = file.size / 1024 / 1024
    if (fileSizeLimit && fileSizeM > fileSizeLimit) {
      Toast.warning(`请上传体积小于${fileSizeLimit}M的附件`)

      file.url = undefined
      return false
    }
    const data = await getOssSignature(file)
    file.url = data.url
    return file
  }

  const onDownload = (file: UploadFile) => {
    const { url, name, type } = file
    if (/^image\//.test(type || '')) {
      window.open(url, '_black')
    } else {
      url ? downloadFile(name, url) : Toast.error('资源不存在')
    }
  }

  const uploadProps: UploadProps = {
    name: 'file',
    fileList,
    action: OSSData ? OSSData['host'] : '',
    headers: {
      'x-oss-object-acl': 'public-read',
      'X-Requested-With': '',
    },
    method: 'POST',
    data: OSSData,
    beforeUpload,
    onChange,
    maxCount: maxCount || 6,
    disabled: !isEdit,
    onPreview: onDownload,
  }

  return (
    <>
      {!isEdit && (!fileList || fileList.length === 0) ? (
        <NoDataLine>-</NoDataLine>
      ) : (
        <StyledUpload
          listType="picture-card"
          {...uploadProps}
        >
          {isEdit && (fileList?.length || 0) < (maxCount || 100) ? (
            <UploadInnerDiv>
              <PlusOutlined />
              <div style={{ marginTop: 8 }}>上传附件</div>
            </UploadInnerDiv>
          ) : null}
        </StyledUpload>
      )}
    </>
  )
}

const StyledUpload = styled(Upload)`
  &&& {
    .ant-upload-list-picture-card-container {
      width: 80px;
      height: 80px;
      border-radius: 8px;
      & .ant-upload-list-item {
        padding: 0;
        border: transparent;
        border-radius: 8px;
        background-color: ${(props) => props.theme.background._4};

        .ant-upload-list-item-thumbnail > img {
          object-fit: cover;
          border-radius: 8px;
        }

        .ant-upload-list-item-name {
          width: 100px;
          padding: 0 5px 5px 5px;
          font-size: 12px;
          color: ${(props) => props.theme.text._2};
        }
      }
    }
    .ant-upload-select-picture-card {
      width: 80px;
      height: 80px;
      border: 1px dashed ${(props) => props.theme.border.greyNormal};
      background-color: ${(props) => props.theme.background._4};
      border-radius: 8px;
      &:hover {
        background-color: ${(props) => props.theme.background._2};
      }
    }
    .ant-upload-list-picture-card .ant-upload-list-item-info::before {
      border-radius: 8px;
      background-color: rgba(21, 37, 41, 0.6);
    }
    .ant-upload-list-item-actions {
      & .anticon-eye svg {
        fill: #ffffff;
      }
      & .ant-btn-text {
        margin-right: 0;
        svg {
          fill: #ffffff;
        }
      }
    }
    .anticon svg {
      fill: ${(props) => props.theme.text._2};
      font-size: 15px;
    }
    .anticon-file {
      position: relative;
      top: -7px;
      & > svg {
        font-size: 12px;
      }
    }
    .anticon-file svg path:first-child {
      fill: transparent;
    }
    .anticon-file svg path:last-child {
      fill: ${(props) => props.theme.text._2};
    }
  }
`

const UploadInnerDiv = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: ${(props) => props.theme.text._2};
`

const NoDataLine = styled.span`
  color: ${(props) => props.theme.text._3};
`
